import { useEffect, useRef, useState } from "react";

import {
  getMarketplaceBrowse,
  type MarketplaceBrowseResponse,
} from "../lib/marketplace-discovery-api";
import {
  getOffersInbox,
  type OffersInbox,
} from "../lib/marketplace-offers-api";
import {
  getCurrentRecoveryWindow,
  type MarketplaceRecoveryWindow,
} from "../lib/marketplace-recovery-api";
import type {
  BuyerConnectionDTO,
  ConnectionRequestDTO,
} from "../lib/marketplace-status";

/**
 * Plan 435 M2 (mfh-hero-component carryover, F1 review fix): the
 * connection + pendingRequest read is NOT owned here any more. It is owned
 * by whichever single `useMarketplaceConnection("agent")` call the caller
 * already has (`DashboardPage` fetches it today for the sidebar/Passport
 * pill) and threaded in as this shape — so mounting `HomeView` never issues
 * a SECOND `GET /api/marketplace/connection` alongside that pre-existing
 * one. The settle gate here still waits on `settled`.
 */
export interface HomeConnectionInput {
  connection: BuyerConnectionDTO | null;
  pendingRequest: ConnectionRequestDTO | null;
  settled: boolean;
  refresh: () => Promise<void>;
}

export interface UseHomeMarketplaceDataResult {
  /**
   * True once the connection, browse, and inbox reads have each resolved OR
   * failed — the Home settle gate (plan 435 M1, mfh-home-settle; tech-spec
   * Concepts "Settling"; decision "the hero settles before it paints").
   * Always true when `enabled` is false (there is nothing to wait for).
   */
  settled: boolean;
  connection: BuyerConnectionDTO | null;
  pendingRequest: ConnectionRequestDTO | null;
  browse: MarketplaceBrowseResponse | null;
  inbox: OffersInbox | null;
  /** Degrades to 0 on a failed/never-loaded browse read — never blocks settle. */
  agentCount: number;
  /** Degrades to 0 on a failed/never-loaded inbox read — never blocks settle. */
  offersCount: number;
  refreshConnection: () => Promise<void>;
  /**
   * Plan 435 M4 (mfh-recovery-card). The buyer's current open recovery
   * window, or null when none/failed/not-yet-loaded — tech-spec
   * Architecture lists this as one of Home's reads, but it is deliberately
   * NOT a settle-gate input (mirrors waitlist/`getAgentDetail`): a slow or
   * failed recovery-window read must never hold up the hero's first paint,
   * since the window only ever ADDS the amber variant + the recovery card
   * on top of whatever the hero already resolved from `settled` data.
   */
  recoveryWindow: MarketplaceRecoveryWindow | null;
  /** Re-fetches the recovery window after a Stay/Continue mutation, so the
   *  card reflects the server's new state without a full reload. */
  refreshRecoveryWindow: () => Promise<void>;
}

/**
 * Plan 435 M1 (mfh-home-settle). Owns the THREE reads that gate Home's hero
 * paint: connection + pendingRequest (via `useMarketplaceConnection`),
 * browse, and the offers inbox. A failed browse/inbox read degrades to zero
 * agents / no offers and never blocks settling (decision: "the hero settles
 * before it paints"). Waitlist and `getAgentDetail` are explicitly NOT
 * settle gates — they belong to later milestones and are not read here.
 *
 * `enabled=false` (the consumer marketplace gate is "loading" or "off")
 * makes the browse/inbox reads no-ops (the effect below returns
 * immediately) — so this hook's OWN reads stay at zero while the gate is
 * closed, by construction. The connection input is the caller's concern.
 */
export function useHomeMarketplaceData(
  getToken: () => Promise<string | null>,
  enabled: boolean,
  connectionInput: HomeConnectionInput,
): UseHomeMarketplaceDataResult {
  const {
    connection,
    pendingRequest,
    refresh: refreshConnection,
    settled: connectionSettled,
  } = connectionInput;

  const [browse, setBrowse] = useState<MarketplaceBrowseResponse | null>(null);
  const [browseSettled, setBrowseSettled] = useState(!enabled);
  const [inbox, setInbox] = useState<OffersInbox | null>(null);
  const [inboxSettled, setInboxSettled] = useState(!enabled);
  const [recoveryWindow, setRecoveryWindow] =
    useState<MarketplaceRecoveryWindow | null>(null);

  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });

  useEffect(() => {
    if (!enabled) {
      // Deferred past a microtask — calling setState synchronously inside
      // an effect body trips the react-hooks/set-state-in-effect lint;
      // mirrors the existing `await Promise.resolve()` lead-in idiom used
      // elsewhere in this codebase for the identical shape.
      void Promise.resolve().then(() => {
        setBrowseSettled(true);
        setInboxSettled(true);
      });
      return;
    }

    let cancelled = false;
    // Deferred past a microtask (react-hooks/set-state-in-effect) — fires
    // once per mount/`enabled` change, well before either read below
    // resolves (each hops through `getToken()` first), so it can never
    // race their `finally` callbacks.
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setBrowseSettled(false);
      setInboxSettled(false);
    });

    void getMarketplaceBrowse(getTokenRef.current)
      .then((result) => {
        if (!cancelled) setBrowse(result);
      })
      .catch(() => {
        if (!cancelled) setBrowse(null);
      })
      .finally(() => {
        if (!cancelled) setBrowseSettled(true);
      });

    void getOffersInbox(getTokenRef.current)
      .then((result) => {
        if (!cancelled) setInbox(result);
      })
      .catch(() => {
        if (!cancelled) setInbox(null);
      })
      .finally(() => {
        if (!cancelled) setInboxSettled(true);
      });

    // Not a settle-gate input (see `recoveryWindow`'s doc comment above) —
    // intentionally no `.finally` toggling either settled flag.
    void getCurrentRecoveryWindow(getTokenRef.current)
      .then((result) => {
        if (!cancelled) setRecoveryWindow(result);
      })
      .catch(() => {
        if (!cancelled) setRecoveryWindow(null);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const refreshRecoveryWindow = async () => {
    if (!enabled) return;
    try {
      setRecoveryWindow(await getCurrentRecoveryWindow(getTokenRef.current));
    } catch {
      setRecoveryWindow(null);
    }
  };

  return {
    settled: !enabled || (connectionSettled && browseSettled && inboxSettled),
    connection,
    pendingRequest,
    browse,
    inbox,
    agentCount: browse?.agents.length ?? 0,
    offersCount: inbox
      ? inbox.made_for_you.length + inbox.available_to_you.length
      : 0,
    refreshConnection,
    recoveryWindow,
    refreshRecoveryWindow,
  };
}
